import { useCallback, useEffect, useRef, useState } from 'react'

// Google Places autocomplete for a single address field, degrading gracefully to a plain
// text input when VITE_GOOGLE_PLACES_API_KEY isn't configured (or Google Maps fails to
// load) — never a hard dependency the rest of the intake form breaks without. The Maps JS
// script (with the Places library) is loaded lazily, once per page, shared across every
// instance of this component via a module-level promise.
//
// Uses the newer AutocompleteSuggestion/Place classes (Places API "New"), not the legacy
// google.maps.places.Autocomplete widget — Google stopped offering the legacy Places API
// for enablement on Cloud projects created after March 2025, so the old widget throws
// "legacy API ... not enabled" on this project no matter what's enabled. The new classes
// only need Places API (New) + Maps JavaScript API, both already enabled here. Since they
// have no built-in dropdown UI, this component renders its own suggestion list to stay a
// plain controlled <input> the rest of the form already depends on.

const API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY as string | undefined

let loadPromise: Promise<void> | null = null

function loadGoogleMapsScript(): Promise<void> {
  if (!API_KEY) return Promise.reject(new Error('no API key'))
  if (loadPromise) return loadPromise
  loadPromise = new Promise((resolve, reject) => {
    if ((window as any).google?.maps?.places?.AutocompleteSuggestion) { resolve(); return }
    const existing = document.querySelector('script[data-google-places]')
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('script failed to load')))
      return
    }
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places&v=weekly`
    script.async = true
    script.defer = true
    script.dataset.googlePlaces = 'true'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('script failed to load'))
    document.head.appendChild(script)
  })
  return loadPromise
}

function suggestionText(suggestion: any): string {
  const text = suggestion?.placePrediction?.text
  if (!text) return ''
  if (typeof text === 'string') return text
  if (typeof text.toString === 'function') {
    const asString = text.toString()
    if (asString && asString !== '[object Object]') return asString
  }
  return text.text ?? ''
}

interface AddressAutocompleteProps {
  label: string
  value: string
  onChange: (formattedAddress: string, placeId: string | null) => void
  disabled?: boolean
  placeholder?: string
}

export function AddressAutocomplete({ label, value, onChange, disabled, placeholder }: AddressAutocompleteProps) {
  const [ready, setReady] = useState(false)
  const [suggestions, setSuggestions] = useState<any[]>([])
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const sessionTokenRef = useRef<any>(null)
  const debounceRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (!API_KEY) return
    let cancelled = false
    loadGoogleMapsScript()
      .then(() => { if (!cancelled) setReady(true) })
      .catch(() => { /* silently fall back to plain text entry */ })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const fetchSuggestions = useCallback(async (input: string) => {
    if (!ready || !input.trim()) { setSuggestions([]); return }
    const google = (window as any).google
    if (!sessionTokenRef.current) sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken()
    try {
      const { suggestions: results } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedRegionCodes: ['ca'],
        sessionToken: sessionTokenRef.current,
      })
      setSuggestions(results ?? [])
      setOpen((results ?? []).length > 0)
    } catch {
      setSuggestions([])
    }
  }, [ready])

  const handleInputChange = (text: string) => {
    onChange(text, null)
    window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => fetchSuggestions(text), 250)
  }

  const handleSelect = async (suggestion: any) => {
    try {
      const place = suggestion.placePrediction.toPlace()
      await place.fetchFields({ fields: ['formattedAddress', 'id'] })
      onChange(place.formattedAddress ?? suggestionText(suggestion), place.id ?? null)
    } catch {
      onChange(suggestionText(suggestion), null)
    }
    setOpen(false)
    setSuggestions([])
    sessionTokenRef.current = null
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ font: '500 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
        {label}
      </div>
      <input
        value={value}
        onChange={e => handleInputChange(e.target.value)}
        onFocus={() => { if (suggestions.length) setOpen(true) }}
        disabled={disabled}
        placeholder={placeholder ?? (API_KEY ? 'Start typing an address…' : 'Street address, city, postal code')}
        className='field-input'
        autoComplete='off'
      />
      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4,
          background: '#fff', border: '1px solid rgba(27,42,74,0.12)', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(27,42,74,0.12)', maxHeight: 220, overflowY: 'auto',
        }}>
          {suggestions.map((s, i) => (
            <div
              key={s.placePrediction?.placeId ?? i}
              onMouseDown={e => e.preventDefault()}
              onClick={() => handleSelect(s)}
              style={{
                padding: '10px 14px', cursor: 'pointer', fontSize: 13.5, color: '#1B2A4A',
                borderBottom: i < suggestions.length - 1 ? '1px solid rgba(27,42,74,0.06)' : 'none',
              }}
            >
              {suggestionText(s)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
